import React, { useEffect, useState } from "react";
import { apiJson } from "../../utils/api";

const AdminUsers = () => {
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null); // null = list view, object = form
  const [form, setForm] = useState({ username: "", password: "", name: "", email: "", roleId: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const fetchData = async () => {
    try {
      const [adminsData, rolesData] = await Promise.all([
        apiJson("/api/admin/admins"),
        apiJson("/api/admin/roles"),
      ]);
      setAdmins(adminsData);
      setRoles(rolesData);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const startCreate = () => {
    setEditing({ id: null });
    setForm({ username: "", password: "", name: "", email: "", roleId: roles[0]?.id || "" });
    setMessage("");
  };

  const startEdit = (admin) => {
    setEditing(admin);
    setForm({
      username: admin.username,
      password: "",
      name: admin.name,
      email: admin.email || "",
      roleId: admin.role?.id || "",
    });
    setMessage("");
  };

  const cancelEdit = () => {
    setEditing(null);
    setMessage("");
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.roleId) {
      setMessage("Name and role are required");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      if (editing.id) {
        const body = { name: form.name, email: form.email, roleId: parseInt(form.roleId) };
        if (form.password) body.password = form.password;
        await apiJson(`/api/admin/admins/${editing.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
      } else {
        if (!form.username.trim() || !form.password) {
          setMessage("Username and password are required for new admin");
          setSaving(false);
          return;
        }
        await apiJson("/api/admin/admins", {
          method: "POST",
          body: JSON.stringify({
            username: form.username,
            password: form.password,
            name: form.name,
            email: form.email,
            roleId: parseInt(form.roleId),
          }),
        });
      }
      setEditing(null);
      await fetchData();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await apiJson(`/api/admin/admins/${admin.id}`, {
        method: "PUT",
        body: JSON.stringify({ isActive: !admin.isActive }),
      });
      await fetchData();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Delete admin "${admin.username}"?`)) return;
    try {
      await apiJson(`/api/admin/admins/${admin.id}`, { method: "DELETE" });
      await fetchData();
    } catch (err) {
      setMessage(err.message);
    }
  };

  if (loading) return <p className="p-4 text-gray-500">Loading...</p>;

  // Form view
  if (editing) {
    return (
      <div className="max-w-lg mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          {editing.id ? "Edit Admin User" : "Create Admin User"}
        </h1>

        {message && <p className="text-red-600 text-sm mb-3">{message}</p>}

        <div className="bg-white shadow rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Username</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              disabled={!!editing.id}
              className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-indigo-200 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Password{editing.id ? " (leave blank to keep current)" : ""}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-indigo-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Full Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-indigo-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-indigo-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Role</label>
            <select
              value={form.roleId}
              onChange={(e) => setForm({ ...form, roleId: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:ring focus:ring-indigo-200"
            >
              <option value="">Select role...</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            onClick={cancelEdit}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Admin Users</h1>
        <button
          onClick={startCreate}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm hover:bg-indigo-700"
        >
          + New Admin
        </button>
      </div>

      {message && <p className="text-red-600 text-sm mb-3">{message}</p>}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 font-medium text-gray-700">Username</th>
              <th className="px-4 py-3 font-medium text-gray-700">Name</th>
              <th className="px-4 py-3 font-medium text-gray-700">Role</th>
              <th className="px-4 py-3 font-medium text-gray-700">Status</th>
              <th className="px-4 py-3 font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {admins.map((a) => (
              <tr key={a.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{a.username}</td>
                <td className="px-4 py-3 text-gray-600">{a.name}</td>
                <td className="px-4 py-3">
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded capitalize">
                    {a.role?.name}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      a.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {a.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3 space-x-2">
                  <button
                    onClick={() => startEdit(a)}
                    className="text-indigo-600 hover:underline text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(a)}
                    className="text-yellow-600 hover:underline text-sm"
                  >
                    {a.isActive ? "Deactivate" : "Activate"}
                  </button>
                  <button
                    onClick={() => handleDelete(a)}
                    className="text-red-600 hover:underline text-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminUsers;
